import axios from 'axios';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError } from '../../utils';
import '../../assets/ops.css';

// One searchable history for products, stock, stickers, fingerprints and
// deliveries. Everything that touches a unit lands here, so any serial or any
// hand-over can be reconstructed after the fact.

const ACTIONS = [
  { value: '', label: 'Everything' },
  { group: 'Products' },
  { value: 'PRODUCT_CREATE', label: 'Product created' },
  { value: 'PRODUCT_UPDATE', label: 'Product updated' },
  { value: 'PRODUCT_DELETE', label: 'Product deleted' },
  { group: 'Stock' },
  { value: 'STOCK_ADD', label: 'Stock added' },
  { value: 'STOCK_VOID', label: 'Stock voided' },
  { group: 'Stickers' },
  { value: 'STICKER_PRINT', label: 'Stickers printed' },
  { value: 'STICKER_REPRINT', label: 'Stickers re-printed' },
  { group: 'Delivery' },
  { value: 'DELIVERY_REQUEST_CREATE', label: 'Request created' },
  { value: 'DELIVERY_REQUEST_CANCEL', label: 'Request cancelled' },
  { value: 'DELIVERY_REQUEST_REJECT', label: 'Request rejected' },
  { value: 'DELIVERY_REQUEST_EDIT', label: 'Quantity edited' },
  { value: 'DELIVERY_OTP_SENT', label: 'OTP sent' },
  { value: 'DELIVERY_OTP_VERIFIED', label: 'OTP verified' },
  { value: 'DELIVERY_OTP_FAILED', label: 'OTP failed' },
  { value: 'DELIVERY_FP_VERIFIED', label: 'Fingerprint verified' },
  { value: 'DELIVERY_FP_FAILED', label: 'Fingerprint failed' },
  { value: 'DELIVERY_FP_OVERRIDE', label: 'Fingerprint overridden' },
  { value: 'DELIVERY_COMPLETE', label: 'Delivery completed' },
  { value: 'ORDER_MANUAL_DELIVER', label: 'Manual delivery' },
  { group: 'Fingerprints' },
  { value: 'FINGERPRINT_ENROLL', label: 'Fingerprint enrolled' },
  { value: 'FINGERPRINT_DELETE', label: 'Fingerprint removed' },
];

// Colour and icon per action, so the log can be skimmed rather than read.
const TONE = {
  PRODUCT_CREATE: ['blue', 'fa-plus-square'],
  PRODUCT_UPDATE: ['grey', 'fa-pencil'],
  PRODUCT_DELETE: ['red', 'fa-trash'],
  STOCK_ADD: ['green', 'fa-cubes'],
  STOCK_VOID: ['red', 'fa-ban'],
  STICKER_PRINT: ['purple', 'fa-print'],
  STICKER_REPRINT: ['amber', 'fa-print'],
  DELIVERY_REQUEST_CREATE: ['blue', 'fa-hand-paper-o'],
  DELIVERY_REQUEST_CANCEL: ['grey', 'fa-times'],
  DELIVERY_REQUEST_REJECT: ['red', 'fa-ban'],
  DELIVERY_REQUEST_EDIT: ['amber', 'fa-pencil'],
  DELIVERY_OTP_SENT: ['blue', 'fa-paper-plane'],
  DELIVERY_OTP_VERIFIED: ['green', 'fa-check'],
  DELIVERY_OTP_FAILED: ['red', 'fa-times'],
  DELIVERY_FP_VERIFIED: ['green', 'fa-hand-paper-o'],
  DELIVERY_FP_FAILED: ['red', 'fa-hand-paper-o'],
  DELIVERY_FP_OVERRIDE: ['amber', 'fa-unlock-alt'],
  DELIVERY_COMPLETE: ['green', 'fa-check-circle'],
  ORDER_MANUAL_DELIVER: ['amber', 'fa-exclamation-triangle'],
  FINGERPRINT_ENROLL: ['purple', 'fa-fingerprint'],
  FINGERPRINT_DELETE: ['red', 'fa-user-times'],
};

const label = (action) =>
  ACTIONS.find((a) => a.value === action)?.label || action.replace(/_/g, ' ').toLowerCase();

export default function InventoryLogScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const [username, setUsername] = useState('');
  const [term, setTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expanded, setExpanded] = useState(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${userInfo?.token}` }),
    [userInfo]
  );

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); setTerm(username.trim()); }, 350);
    return () => clearTimeout(timer);
  }, [username]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${api}/api/stock/logs`, {
        headers,
        params: { page, action, username: term, startDate, endDate, pageSize: 40 },
      });
      setRows(data);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  }, [headers, page, action, term, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const clear = () => {
    setAction('');
    setUsername('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  return (
    <div className="ops">
      <Helmet><title>Stock & Delivery Log</title></Helmet>

      <div className="ops-head">
        <div>
          <h2>Stock &amp; Delivery Log</h2>
          <p>Every product change, stock movement, sticker print and delivery step, newest first.</p>
        </div>
        <button className="ops-btn" onClick={load} disabled={loading}>
          <i className="fa fa-refresh" /> Refresh
        </button>
      </div>

      <div className="ops-card">
        <div className="ops-grid">
          <div>
            <label className="ops-label">Action</label>
            <select
              className="ops-select"
              value={action}
              onChange={(e) => { setAction(e.target.value); setPage(1); }}
            >
              {ACTIONS.map((item, index) =>
                item.group ? (
                  <optgroup key={`g-${index}`} label={item.group} />
                ) : (
                  <option key={item.value} value={item.value}>{item.label}</option>
                )
              )}
            </select>
          </div>
          <div>
            <label className="ops-label">User or staff</label>
            <input
              className="ops-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username…"
            />
          </div>
          <div>
            <label className="ops-label">From</label>
            <input
              className="ops-input"
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            />
          </div>
          <div>
            <label className="ops-label">To</label>
            <input
              className="ops-input"
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            />
          </div>
        </div>
        {(action || username || startDate || endDate) && (
          <button className="ops-btn sm ghost" style={{ marginTop: 10 }} onClick={clear}>
            <i className="fa fa-times" /> Clear filters
          </button>
        )}
      </div>

      {loading && !rows ? (
        <div className="ops-empty"><i className="fa fa-spinner fa-spin" />Loading log…</div>
      ) : !rows?.rows?.length ? (
        <div className="ops-empty"><i className="fa fa-file-text-o" />Nothing matches these filters.</div>
      ) : (
        <>
          <p style={{ color: 'var(--ops-muted)', fontSize: 13, margin: '0 0 10px' }}>
            {rows.total.toLocaleString()} entries
          </p>
          <div className="ops-table-wrap">
            <table className="ops-table" style={{ minWidth: 860 }}>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>When</th>
                  <th style={{ width: 170 }}>Action</th>
                  <th>What happened</th>
                  <th className="ops-hide-sm" style={{ width: 130 }}>By</th>
                  <th className="num" style={{ width: 60 }}>Qty</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {rows.rows.map((row) => {
                  const [tone, icon] = TONE[row.action] || ['grey', 'fa-circle-o'];
                  const open = expanded === row._id;
                  const hasDetail =
                    row.serials?.length || row.changes?.length || row.meta;
                  return (
                    <Fragment key={row._id}>
                      <tr>
                        <td style={{ fontSize: 12, color: 'var(--ops-muted)', whiteSpace: 'nowrap' }}>
                          {new Date(row.createdAt).toLocaleString()}
                        </td>
                        <td>
                          <span className={`ops-badge ${tone}`}>
                            <i className={`fa ${icon}`} /> {label(row.action)}
                          </span>
                        </td>
                        <td style={{ fontSize: 13 }}>{row.message}</td>
                        <td className="ops-hide-sm" style={{ fontSize: 12.5 }}>
                          {row.actorName || '—'}
                          {row.actorRole && (
                            <div style={{ fontSize: 10.5, color: 'var(--ops-muted)' }}>{row.actorRole}</div>
                          )}
                        </td>
                        <td className="num">{row.quantity || ''}</td>
                        <td>
                          {hasDetail ? (
                            <button
                              className="ops-btn sm ghost"
                              onClick={() => setExpanded(open ? null : row._id)}
                              title="Details"
                            >
                              <i className={`fa ${open ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={6} style={{ background: '#f8fafc' }}>
                            <LogDetail row={row} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {rows.pages > 1 && (
            <div className="ops-pager">
              <button className="ops-btn sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <span>Page {rows.page} of {rows.pages}</span>
              <button className="ops-btn sm" disabled={page >= rows.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function LogDetail({ row }) {
  return (
    <div style={{ padding: '4px 0', display: 'grid', gap: 12 }}>
      {row.changes?.length > 0 && (
        <div>
          <div className="ops-label">Changes</div>
          <div style={{ display: 'grid', gap: 4 }}>
            {row.changes.map((change, index) => (
              <div key={index} style={{ fontSize: 12.5 }}>
                <strong>{change.label || change.field}:</strong>{' '}
                <span style={{ color: 'var(--ops-red)', textDecoration: 'line-through' }}>
                  {String(change.before ?? '—')}
                </span>{' '}
                → <span style={{ color: 'var(--ops-green)' }}>{String(change.after ?? '—')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {row.serials?.length > 0 && (
        <div>
          <div className="ops-label">Serials ({row.serials.length})</div>
          <div className="ops-mono" style={{ fontSize: 11.5, color: 'var(--ops-muted)', wordBreak: 'break-all' }}>
            {row.serials.slice(0, 60).join(', ')}
            {row.serials.length > 60 && ` … and ${row.serials.length - 60} more`}
          </div>
        </div>
      )}

      {row.meta && (
        <div>
          <div className="ops-label">Details</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {Object.entries(row.meta)
              .filter(([, value]) => value !== '' && value !== null && value !== undefined)
              .map(([key, value]) => (
                <span key={key} className="ops-badge grey">
                  {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </span>
              ))}
          </div>
        </div>
      )}

      {row.targetUsername && (
        <div style={{ fontSize: 12.5, color: 'var(--ops-muted)' }}>
          Customer: <strong>{row.targetUsername}</strong>
          {row.ip && ` · IP ${row.ip}`}
        </div>
      )}
    </div>
  );
}
